import "./App.css";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useWorkspace } from "./context/WorkspaceContext";
import {
  resolveQaAgentCapabilities,
  resolveQaAgentId,
  resolveQaProductAgentIds,
} from "./config/qaAgents";
import {
  AgenticNarrowChat,
  type OnBeforeRunProvider,
} from "@trimble-agentic-internal-npm-local/agentic-chat-react";
import { refreshAccessToken } from "./services/workspaceService";
import { resolveChatFeatures } from "./config/chatFeatures";
import { SelectionContextBanner } from "./components/SelectionContextBanner";
import { ClientToolsPanel } from "./components/ClientToolsPanel";
import { AppStatus } from "./components/AppStatus";
import { Button } from "./components/ui/button";
import { RetiredAppNotice } from "./components/RetiredAppNotice";
import type { SelectionInfo } from "./tools/selectionApiTypes";
import { useAccessibleModules } from "./hooks/useAccessibleModules";
import { useClientTools } from "./hooks/useClientTools";

function App() {
  const { isLoading, isReady, isRetired, tokenScopes, error, api, hostInfo } =
    useWorkspace();
  const accessibleModules = useAccessibleModules(isReady);
  const { slug } = useParams<{ slug?: string }>();
  const environment = (import.meta.env.VITE_ENVIRONMENT ?? "prod") as
    | "prod"
    | "stage"
    | "dev";
  // dev uses the stage Agentic Platform but with its own agent IDs
  const chatEnvironment: "prod" | "stage" =
    environment === "dev" ? "stage" : environment;
  const envAgentId = import.meta.env.VITE_AGENT_ID ?? null;
  const defaultAgentId = slug
    ? resolveQaAgentId(slug, environment)
    : envAgentId;
  const {
    selectionContext: selectionContextEnabled,
    clientTools: clientToolsEnabled,
    showAgentDropdown,
  } = resolveQaAgentCapabilities(slug);
  const productAgents = useMemo(
    () => resolveQaProductAgentIds(environment),
    [environment],
  );
  const chatFeatures = useMemo(
    () => resolveChatFeatures(tokenScopes, environment === "dev"),
    [tokenScopes, environment],
  );
  const {
    runTimeTools,
    bindingComplete: clientToolBindingComplete,
    hasPreviewToolBound,
  } = useClientTools({ api, isReady, enabled: clientToolsEnabled });

  const [currentSelection, setCurrentSelection] =
    useState<SelectionInfo | null>(null);

  const assistUrls: Record<"prod" | "stage", string> = {
    prod: "https://assist.ai.trimble.com",
    stage: "https://assist.stage.trimble-ai.com",
  };

  // Memoize props passed to AgenticNarrowChat so its internal useEffect
  // (which depends on [url, config, provideToken, onBeforeRun]) doesn't
  // tear down and re-create the iframe listener on every render.
  const provideToken = useCallback(() => refreshAccessToken(), []);
  const chatInputConfig = useMemo(
    () => ({ buttons: [] as never[], hideModelSelection: false }),
    [],
  );

  const globalTools = useMemo(() => ({}), []);
  const hostTools = useMemo(() => Object.values(runTimeTools), [runTimeTools]);

  // Build the context array, optionally including the host selection.
  const fullContext = useMemo(() => {
    const ctx: Array<{ description: string; value: string }> = [];
    if (
      hostInfo?.name &&
      !ctx.some((entry) => entry.description.toLowerCase() === "productname")
    ) {
      ctx.push({
        description: "productName",
        value: hostInfo.name,
      });
    }
    const isQuadriWithNovapoint =
      hostInfo?.name?.trim().toLowerCase() === "quadri with novapoint";
    if (isQuadriWithNovapoint && accessibleModules.length > 0) {
      ctx.push({
        description: "novapointAccessibleModules",
        value: JSON.stringify(accessibleModules),
      });
    }
    if (
      selectionContextEnabled &&
      currentSelection &&
      currentSelection.count > 0
    ) {
      ctx.push({
        description: `Currently selected items in ${hostInfo?.name ?? "the host application"}`,
        value: JSON.stringify(currentSelection),
      });
    }
    return ctx;
  }, [selectionContextEnabled, currentSelection, hostInfo, accessibleModules]);

  const onBeforeRunConfig = useMemo<OnBeforeRunProvider>(
    () => async () => ({
      tools: {
        runTime: runTimeTools,
        global: globalTools,
      },
      runContext: {
        context: fullContext,
      },
    }),
    [runTimeTools, globalTools, fullContext],
  );

  const handleSelectionChange = useCallback(
    (sel: SelectionInfo | null) => setCurrentSelection(sel),
    [],
  );

  // Show loading state
  if (isLoading) {
    return (
      <AppStatus title="Connecting..." detail="Initializing Workspace API" />
    );
  }

  if (isRetired) {
    return <RetiredAppNotice hostName={hostInfo?.name} />;
  }

  // Show error state
  if (error) {
    return (
      <AppStatus
        title="Connection Error"
        isError
        detail={
          <>
            Make sure this extension is running inside a Trimble product. The
            agents can also be accessed via{" "}
            <Button
              variant="link"
              render={
                <a
                  href={
                    assistUrls[chatEnvironment] + "/agent/" + defaultAgentId
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                />
              }
              nativeButton={false}
            >
              Trimble Assist
            </Button>
          </>
        }
      ></AppStatus>
    );
  }

  // Main application - show when ready
  if (!isReady) {
    return <AppStatus title="Initializing..." />;
  }

  if (clientToolsEnabled && !clientToolBindingComplete) {
    return (
      <AppStatus title="Connecting..." detail="Initializing client tools" />
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {selectionContextEnabled && (
        <SelectionContextBanner
          isReady={isReady}
          onSelectionChange={handleSelectionChange}
        />
      )}
      {clientToolsEnabled && (
        <ClientToolsPanel
          runTimeTools={runTimeTools}
          globalTools={globalTools}
          hasPreviewToolBound={hasPreviewToolBound}
        />
      )}
      <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
        <AgenticNarrowChat
          provideToken={provideToken}
          environment={chatEnvironment}
          theme="light"
          chatInputConfiguration={chatInputConfig}
          runConfig={onBeforeRunConfig}
          hostTools={hostTools}
          agentId={defaultAgentId!}
          features={chatFeatures}
          productAgents={productAgents}
          showCloseButton={false}
          showAgentDropdown={showAgentDropdown}
        />
      </div>
    </div>
  );
}

export default App;
